import { Directive, EventEmitter, inject, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { filter, map, Observable, take } from 'rxjs';

import {
  getResidentAppSettings,
  getTicketSettings,
  getTicketSorting,
  IConsumptionState,
} from '../../../+state';
import * as fromMasterDataState from '../../../+state/account/masterdata';
import * as fromTicketsState from '../../../+state/tickets';
import {
  ContractStatus,
  IContract,
  ImmomioIconName,
  ISort,
  LoadingType,
  SortType,
  TicketingType,
  TicketingVersion,
  TicketIssueType,
  TicketSortField,
} from '../../../models';
import { contractStatusConverter, CustomerService, RouterService } from '../../../services';

@UntilDestroy()
@Directive()
export abstract class TicketsOverviewDamageBaseComponent {
  @Output() openTicket = new EventEmitter();

  private routerService = inject(RouterService);
  private route = inject(ActivatedRoute);
  protected ticketsStore = inject<Store<fromTicketsState.ITicketsState | IConsumptionState>>(Store);
  private masterDataStore = inject<Store<fromMasterDataState.IMasterDataState>>(Store);
  private customerService = inject(CustomerService);

  public damageTickets$ = this.ticketsStore.select(
    fromTicketsState.getActiveDamageTicketsForSelectedContract
  );
  public hasNextPage$ = this.ticketsStore.select(
    fromTicketsState.hasNextPageActiveDamageTicketsForSelectedContract
  );
  public damageTicketsActionState$ = this.ticketsStore.select(
    fromTicketsState.getActiveDamageTicketsForSelectedContractActionState
  );
  public ticketingVersion$: Observable<TicketingVersion> = this.ticketsStore
    .select(getResidentAppSettings)
    .pipe(
      map(response => response?.ticketingVersion),
      filter(response => response !== null)
    );
  public selectedSorting$ = this.masterDataStore.select(getTicketSorting);
  public selectedContractIsActive = true;
  public immomioIconName = ImmomioIconName;
  public numberOfContracts = this.masterDataStore
    .select(fromMasterDataState.getContractsCount)
    .pipe(untilDestroyed(this));
  public loadingTypes: LoadingType[] = [];
  public selectedContract$ = this.masterDataStore
    .select(fromMasterDataState.getSelectedContract)
    .pipe(untilDestroyed(this));
  public sort: ISort<TicketSortField>;
  public ticketSettings$ = this.masterDataStore.select(getTicketSettings);
  public onlyExpiredContractsAndActiveMembership$: Observable<boolean> = this.customerService
    .onlyExpiredContractsAndActiveMembership()
    .pipe(take(1));
  protected readonly sortTypes = SortType;
  protected readonly ticketingVersion = TicketingVersion;
  protected readonly ticketingType = TicketingType;
  protected pagination = {
    offset: 0,
    limit: 20,
  };

  constructor() {
    this.selectedContract$.pipe(untilDestroyed(this)).subscribe(res => {
      this.selectedContractIsActive = res?.status !== ContractStatus.ENDED;
    });
  }

  public createTicket() {
    this.masterDataStore
      .select(fromMasterDataState.getContracts)
      .pipe(
        map((contracts: IContract[]) =>
          contracts?.filter(contract => contractStatusConverter.isContractActive(contract))
        ),
        take(1)
      )
      .subscribe({
        next: res => {
          if (res.length === 1) {
            // prevent expired Contract to be used for ticket creation
            this.masterDataStore.dispatch(fromMasterDataState.SelectContract({ id: res[0].id }));
            this.goToRoute('ticket-creation');
          } else if (res.length >= 2) {
            this.goToRoute('select-contract');
          } else {
            this.goToRoute('ticket-creation');
          }
        },
        error: () => this.goToRoute('ticket-creation'),
      });
  }

  public loadMoreDamageTickets(): void {
    this.pagination = { offset: this.pagination.offset + 20, limit: 20 };

    this.ticketingVersion$.pipe(take(1)).subscribe(version => {
      if (version === TicketingVersion.HIERARCHICAL) {
        this.ticketsStore.dispatch(
          fromTicketsState.LoadActiveDamageTicketsForSelectedContractHQ({
            ...this.pagination,
            sort: this.sort,
          })
        );
      } else if (version === TicketingVersion.METADATA) {
        this.ticketsStore.dispatch(
          fromTicketsState.LoadActiveDamageTicketsForSelectedContract({
            ...this.pagination,
            sort: this.sort,
          })
        );
      }
    });
  }

  private goToRoute(path: string) {
    void this.routerService.navigate([`./${path}`, TicketIssueType.PROPERTY_DAMAGE], {
      relativeTo: this.route,
    });
  }
}
